import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import addDays from "date-fns/addDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import subMonths from "date-fns/subMonths";
import subYears from "date-fns/subYears";

export default function DateRangePicker({ initialRange, onApply, onCancel }) {
  const fmt = (date) => (date ? date.toISOString().substring(0, 10) : "");
  const [fromDate, setFromDate] = useState(fmt(initialRange.from));
  const [toDate, setToDate] = useState(fmt(initialRange.to));

  const containerRef = useRef(null);
  // close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onCancel();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onCancel]);

  const apply = () => {
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    onApply({ from, to });
  };

  const clear = () => {
    setFromDate("");
    setToDate("");
  };

  const selectPreset = ({ startDate, endDate }) => {
    setFromDate(fmt(startDate));
    setToDate(fmt(endDate));
  };

  return (
    <Container ref={containerRef}>
      <Sidebar>
        <Title>Filters</Title>
        <Divider />
        {presets.map((p) => (
          <Preset key={p.label} onClick={() => selectPreset(p.range())}>
            {p.label}
          </Preset>
        ))}
      </Sidebar>
      <Main>
        <Field>
          <Label>From</Label>
          <InputDate
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </Field>
        <Field>
          <Label>To</Label>
          <InputDate
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </Field>
        <Footer>
          <ButtonOutline onClick={clear}>Reset</ButtonOutline>
          <ButtonSolid onClick={apply}>Apply</ButtonSolid>
        </Footer>
      </Main>
    </Container>
  );
}

const presets = [
  {
    label: "Today",
    range: () => ({ startDate: new Date(), endDate: new Date() }),
  },
  {
    label: "Yesterday",
    range: () => ({
      startDate: addDays(new Date(), -1),
      endDate: addDays(new Date(), -1),
    }),
  },
  {
    label: "This week",
    range: () => ({
      startDate: startOfWeek(new Date()),
      endDate: endOfWeek(new Date()),
    }),
  },
  {
    label: "Last week",
    range: () => {
      const start = addDays(startOfWeek(new Date()), -7);
      const end = addDays(endOfWeek(new Date()), -7);
      return { startDate: start, endDate: end };
    },
  },
  {
    label: "This month",
    range: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
    }),
  },
  {
    label: "Last month",
    range: () => {
      const last = subMonths(new Date(), 1);
      return { startDate: startOfMonth(last), endDate: endOfMonth(last) };
    },
  },
  {
    label: "This year",
    range: () => ({
      startDate: new Date(new Date().getFullYear(), 0, 1),
      endDate: new Date(new Date().getFullYear(), 11, 31),
    }),
  },
  {
    label: "Last year",
    range: () => {
      const last = subYears(new Date(), 1);
      return {
        startDate: new Date(last.getFullYear(), 0, 1),
        endDate: new Date(last.getFullYear(), 11, 31),
      };
    },
  },
];

const Container = styled.div`
  display: flex;
  width: 400px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 140px;
  padding: 12px;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const Divider = styled.hr`
  margin: 8px 0;
  border: none;
  border-top: 1px solid #e9ecef;
`;

const Preset = styled.div`
  padding: 6px 0;
  cursor: pointer;
  font-size: 14px;
  color: #161718;
  &:hover {
    color: #004f96;
  }
`;

const Main = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  margin-bottom: 4px;
`;

const InputDate = styled.input`
  padding: 6px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  cursor: text;

  /* make the native calendar icon show a pointer */
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
  }
  &::-moz-calendar-picker-indicator {
    cursor: pointer;
  }
`;

const Footer = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const ButtonOutline = styled.button`
  padding: 6px 12px;
  border: 1px solid #ced4da;
  background: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  color: #004f96;
`;

const ButtonSolid = styled.button`
  padding: 6px 12px;
  border: none;
  background: #0065c0;
  color: white;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
`;
